import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormState, useWatch, type Control, type UseFormSetValue } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { TableCell, TableRow } from '@/components/ui/table';
import type { ReceiveConfirmLineDto } from '@/entities/receive';
import { formatDisplayDate } from '@/shared/lib';
import { cn } from '@/lib/utils';

import {
  formatQty,
  fromDisplayQty,
  getExpectedTotal,
  getFieldCeiling,
  getRemainingQty,
  getUnitLabel,
  redistributeQuantities,
  supportsHandlingUnits,
  toDisplayQty
} from '../lib/quantities';
import { CONFIRM_ARRIVAL_QUANTITY_FIELDS, CONFIRM_ARRIVAL_UNIT_MODE } from '../model/constants';
import type { ConfirmArrivalFormValues, ConfirmArrivalQuantityField, ConfirmArrivalUnitMode } from '../model/types';
import { ConfirmArrivalInventoryQty } from './confirm-arrival-inventory-qty';
import { ConfirmArrivalQuantityInput } from './confirm-arrival-quantity-input';

interface ConfirmArrivalLineRowProps {
  control: Control<ConfirmArrivalFormValues>;
  setValue: UseFormSetValue<ConfirmArrivalFormValues>;
  line: ReceiveConfirmLineDto;
  index: number;
  unitMode: ConfirmArrivalUnitMode;
  showLostColumn: boolean;
  fieldLabels: Record<ConfirmArrivalQuantityField, string>;
}

/**
 * One consignment line.
 *
 * Subscribes to its **own** slice of the form (`lines.<i>`) rather than reading
 * the array from the parent: typing in one row then re-renders that row alone,
 * not the whole table.
 */
export function ConfirmArrivalLineRow({
  control,
  setValue,
  line,
  index,
  unitMode,
  showLostColumn,
  fieldLabels
}: ConfirmArrivalLineRowProps) {
  const { t } = useTranslation();
  const values = useWatch({ control, name: `lines.${index}` });
  const { errors } = useFormState({ control, name: `lines.${index}` });

  // One write per edit: the three buckets are rebalanced together, so they are
  // set as a whole row rather than field by field.
  const handleQuantityChange = useCallback(
    (field: ConfirmArrivalQuantityField, displayValue: number) => {
      if (!values) return;
      const next = redistributeQuantities(line, values, field, fromDisplayQty(line, unitMode, displayValue));
      setValue(`lines.${index}`, next, { shouldDirty: true, shouldValidate: true });
    },
    [values, line, unitMode, setValue, index]
  );

  if (!values) return null;

  const unitLabel = getUnitLabel(line, unitMode);
  const remaining = getRemainingQty(line, values);
  const lineErrors = errors.lines?.[index];
  const isHandlingMode = unitMode === CONFIRM_ARRIVAL_UNIT_MODE.HANDLING;
  const showInventoryQty = isHandlingMode && supportsHandlingUnits(line);

  return (
    <TableRow className="align-top">
      <TableCell>
        <div className="flex flex-col gap-1">
          <span className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">{line.lineReference}</Badge>
            <span className="text-sm text-foreground">{line.productDescription}</span>
          </span>
          {line.ownerItemDescription && (
            <span className="text-xs text-muted-foreground">{line.ownerItemDescription}</span>
          )}
          <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {line.batchNumber && <span>{line.batchNumber}</span>}
            {line.expiryDate && <span>{formatDisplayDate(line.expiryDate)}</span>}
            {line.dangerousGood && <Badge variant="destructive">UN {line.dangerousGood.unId}</Badge>}
          </span>
        </div>
      </TableCell>

      <TableCell>
        <span className="flex flex-col text-sm tabular-nums">
          <span>
            {formatQty(toDisplayQty(line, unitMode, getExpectedTotal(line)))} {unitLabel}
          </span>
          {supportsHandlingUnits(line) && !isHandlingMode && (
            <span className="text-xs text-muted-foreground">
              {t('receive.confirm.inBundle', 'in {{bundle}}', { bundle: line.bundleTypeDescription ?? '' })}
            </span>
          )}
          {showInventoryQty && (
            <ConfirmArrivalInventoryQty quantity={getExpectedTotal(line)} unitLabel={line.ltuTypeDescription} />
          )}
        </span>
      </TableCell>

      {CONFIRM_ARRIVAL_QUANTITY_FIELDS.map((field) => {
        if (field === 'lostQty' && !showLostColumn) return null;
        const fieldError = lineErrors?.[field]?.message;

        return (
          <TableCell key={field}>
            <ConfirmArrivalQuantityInput
              id={`${line.lineId}-${field}`}
              aria-label={`${line.lineReference} — ${fieldLabels[field]}`}
              value={toDisplayQty(line, unitMode, values[field])}
              max={toDisplayQty(line, unitMode, getFieldCeiling(line, field))}
              onChange={(next) => handleQuantityChange(field, next)}
              unitLabel={unitLabel}
              invalid={Boolean(fieldError)}
            />
            {showInventoryQty && (
              <ConfirmArrivalInventoryQty
                className="mt-1"
                quantity={values[field]}
                unitLabel={line.ltuTypeDescription}
              />
            )}
            {fieldError && <p className="mt-1 max-w-50 text-xs text-destructive">{fieldError}</p>}
          </TableCell>
        );
      })}

      <TableCell>
        <span className="flex flex-col">
          <span
            className={cn(
              'text-sm tabular-nums',
              remaining < 0 ? 'font-medium text-destructive' : 'text-muted-foreground'
            )}>
            {formatQty(toDisplayQty(line, unitMode, remaining))} {unitLabel}
          </span>
          {showInventoryQty && <ConfirmArrivalInventoryQty quantity={remaining} unitLabel={line.ltuTypeDescription} />}
        </span>
      </TableCell>
    </TableRow>
  );
}
